import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { io, Socket } from 'socket.io-client';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBlcOaGsV6wV6u-bd1onOicoZPBUbX8RE8';
const API_BASE = 'https://weridesudan.com/api/admin';
const SOCKET_URL = 'https://weridesudan.com';
const DEFAULT_CENTER = { lat: 15.5007, lng: 32.5599 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const POLL_INTERVAL = 5000;
const PULSE_TTL = 10000;
const EN_ROUTE_CACHE_TTL = 25_000;

// Statuses that count as "active" — must match the backend $in list exactly
const ACTIVE_STATUSES = ['searching', 'accepted', 'en_route', 'arrived', 'in_progress'];
// Blue route: driver is heading to pickup
const EN_ROUTE_STATUSES = ['accepted', 'en_route'];

interface Driver {
  _id: string;
  name: string;
  phone: string;
  isAvailable: boolean;
  location: { coordinates: [number, number] }; // GeoJSON [lng, lat]
}

// Ride coords come from the Ride model as plain {lat, lng} objects, not GeoJSON arrays
interface RideCoords {
  lat: number;
  lng: number;
}

interface Ride {
  _id: string;
  status: string;
  pickup: { coordinates?: RideCoords; address?: string };
  destination: { coordinates?: RideCoords; address?: string }; // backend field is "destination"
  rider?: { name: string };
  driver?: { _id?: string; name: string };
}

type ConnStatus = 'connecting' | 'live' | 'polling' | 'error';

// For driver location (GeoJSON [lng, lat] array)
const toLatLng = (coords?: [number, number]): google.maps.LatLngLiteral | null => {
  if (!coords || coords.length < 2) return null;
  return { lat: coords[1], lng: coords[0] };
};

// For ride pickup/destination ({lat, lng} plain object from Ride model)
const rideCoord = (c?: RideCoords): google.maps.LatLngLiteral | null => {
  if (!c || typeof c.lat !== 'number' || typeof c.lng !== 'number') return null;
  return { lat: c.lat, lng: c.lng };
};

const pinIcon = (color: string) => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="38" viewBox="0 0 30 38">
      <path d="M15 0C6.716 0 0 6.716 0 15c0 9.665 13.5 22.5 14.375 23.344a.875.875 0 0 0 1.25 0C16.5 37.5 30 24.665 30 15 30 6.716 23.284 0 15 0z" fill="${color}"/>
      <circle cx="15" cy="15" r="6.5" fill="white"/>
      <circle cx="15" cy="15" r="3.5" fill="${color}"/>
    </svg>`
  )}`,
  scaledSize: new window.google.maps.Size(30, 38),
  anchor: new window.google.maps.Point(15, 38),
});

const makeDriverIcon = (isAvailable: boolean, pulse: boolean) => {
  const color = isAvailable ? '#22c55e' : '#f97316';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
    ${pulse ? `<circle cx="18" cy="18" r="16" fill="${color}" opacity="0.2">
      <animate attributeName="r" values="9;17;9" dur="1.4s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.2;0;0.2" dur="1.4s" repeatCount="indefinite"/>
    </circle>` : ''}
    <circle cx="18" cy="18" r="9" fill="${color}" stroke="white" stroke-width="2.5"/>
    <circle cx="18" cy="18" r="4" fill="white"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(36, 36),
    anchor: new window.google.maps.Point(18, 18),
  };
};

const LiveMap: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [rides, setRides] = useState<Ride[]>([]);
  const [routePaths, setRoutePaths] = useState<Map<string, google.maps.LatLng[]>>(new Map());
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState('');
  const [connStatus, setConnStatus] = useState<ConnStatus>('connecting');
  const [recentlyMoved, setRecentlyMoved] = useState<Set<string>>(new Set());
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [adminLocation, setAdminLocation] = useState<{ lat: number; lng: number } | null>(null);

  const mapRef = useRef<google.maps.Map | null>(null);
  const prevDriverCountRef = useRef(0);
  const hasFitRef = useRef(false);
  const driversRef = useRef<Driver[]>([]);
  const ridesRef = useRef<Ride[]>([]);
  const routeCacheRef = useRef<Map<string, { path: google.maps.LatLng[]; ts: number }>>(new Map());
  const socketRef = useRef<Socket | null>(null);
  const prevPositionsRef = useRef<Map<string, [number, number]>>(new Map());
  const pulseTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      pos => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setAdminLocation(loc);
        if (prevDriverCountRef.current === 0) setCenter(loc);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  }, []);

  const markMoved = useCallback((driverId: string) => {
    setRecentlyMoved(prev => { const s = new Set(prev); s.add(driverId); return s; });
    const existing = pulseTimersRef.current.get(driverId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      setRecentlyMoved(prev => { const s = new Set(prev); s.delete(driverId); return s; });
      pulseTimersRef.current.delete(driverId);
    }, PULSE_TTL);
    pulseTimersRef.current.set(driverId, t);
  }, []);

  const applyDriverUpdate = useCallback((newDrivers: Driver[]) => {
    driversRef.current = newDrivers;
    newDrivers.forEach(d => {
      if (!d.location?.coordinates) return;
      const prev = prevPositionsRef.current.get(d._id);
      const [lng, lat] = d.location.coordinates;
      if (!prev || prev[0] !== lng || prev[1] !== lat) {
        markMoved(d._id);
        prevPositionsRef.current.set(d._id, [lng, lat]);
      }
    });
    setDrivers(newDrivers);
  }, [markMoved]);

  const applyRideUpdate = useCallback((newRides: Ride[]) => {
    ridesRef.current = newRides;
    setRides(newRides);
  }, []);

  const fitAllBounds = useCallback((driverList: Driver[]) => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    let pointCount = 0;
    driverList.forEach(d => {
      if (d.location?.coordinates?.length === 2) {
        bounds.extend({ lat: d.location.coordinates[1], lng: d.location.coordinates[0] });
        pointCount++;
      }
    });
    ridesRef.current.forEach(r => {
      const p = rideCoord(r.pickup?.coordinates);
      const d = rideCoord(r.destination?.coordinates);
      if (p) { bounds.extend(p); pointCount++; }
      if (d) { bounds.extend(d); pointCount++; }
    });
    if (pointCount === 0) return;
    map.fitBounds(bounds, 80);
    const validDriverCount = driverList.filter(d => d.location?.coordinates?.length === 2).length;
    if (validDriverCount <= 1 && ridesRef.current.length === 0) {
      window.google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 15) map.setZoom(15);
      });
    }
    hasFitRef.current = true;
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE}/map-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      applyDriverUpdate(data.drivers || []);
      applyRideUpdate(data.activeRides || []);
      setLastUpdated(new Date().toLocaleTimeString());
      setError('');
    } catch {
      setError('Could not load map data');
      setConnStatus('error');
    }
  }, [applyDriverUpdate, applyRideUpdate]);

  useEffect(() => {
    const validCount = drivers.filter(d => d.location?.coordinates?.length === 2).length;
    if (validCount > 0 && (!hasFitRef.current || validCount > prevDriverCountRef.current)) {
      fitAllBounds(drivers);
    }
    if (validCount > 0) prevDriverCountRef.current = validCount;
  }, [drivers, fitAllBounds]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      timeout: 8000,
    });
    socketRef.current = socket;

    socket.on('connect', () => { setConnStatus('live'); setError(''); });
    socket.on('disconnect', () => setConnStatus('polling'));
    socket.on('connect_error', () => setConnStatus('polling'));

    socket.on('driverLocationUpdate', ({ driverId, lat, lng }: { driverId: string; lat: number; lng: number }) => {
      markMoved(driverId);
      prevPositionsRef.current.set(driverId, [lng, lat]);
      setDrivers(prev => {
        const next = prev.map(d =>
          d._id === driverId ? { ...d, location: { coordinates: [lng, lat] as [number, number] } } : d
        );
        driversRef.current = next;
        return next;
      });
      setLastUpdated(new Date().toLocaleTimeString());
    });

    socket.on('mapUpdate', (data: { drivers: Driver[]; activeRides: Ride[] }) => {
      applyDriverUpdate(data.drivers || []);
      applyRideUpdate(data.activeRides || []);
      setLastUpdated(new Date().toLocaleTimeString());
    });

    return () => {
      socket.disconnect();
      pulseTimersRef.current.forEach(t => clearTimeout(t));
    };
  }, [markMoved, applyDriverUpdate, applyRideUpdate]);

  // Fetch real road routes from Directions API for active rides
  useEffect(() => {
    if (!isLoaded) return;
    const now = Date.now();
    const svc = new window.google.maps.DirectionsService();

    rides.forEach(r => {
      if (!ACTIVE_STATUSES.includes(r.status)) return;
      const isEnRoute = EN_ROUTE_STATUSES.includes(r.status);
      const cacheKey = `${r._id}_${isEnRoute ? 'en' : 'ip'}`;
      const cached = routeCacheRef.current.get(cacheKey);

      if (cached && (!isEnRoute || now - cached.ts < EN_ROUTE_CACHE_TTL)) {
        setRoutePaths(prev => prev.get(r._id) === cached.path ? prev : new Map(prev).set(r._id, cached.path));
        return;
      }

      let origin: google.maps.LatLngLiteral | null = null;
      let dest: google.maps.LatLngLiteral | null = null;

      if (isEnRoute) {
        // Use ref (always current) + String() cast to handle ObjectId vs string comparison
        const drvId = String(r.driver?._id ?? '');
        const drv = driversRef.current.find(d => String(d._id) === drvId);
        if (!drv?.location?.coordinates) return;
        origin = { lat: drv.location.coordinates[1], lng: drv.location.coordinates[0] };
        dest = rideCoord(r.pickup.coordinates);
      } else {
        origin = rideCoord(r.pickup.coordinates);
        dest = rideCoord(r.destination?.coordinates);
      }
      if (!origin || !dest) return;

      svc.route(
        { origin, destination: dest, travelMode: window.google.maps.TravelMode.DRIVING },
        (result, routeStatus) => {
          if (routeStatus !== 'OK' || !result?.routes[0]) return;
          const path = result.routes[0].overview_path;
          routeCacheRef.current.set(cacheKey, { path, ts: Date.now() });
          setRoutePaths(prev => new Map(prev).set(r._id, path));
        }
      );
    });
  }, [rides, drivers, isLoaded]);

  // A ride is "valid" (renderable) when both pickup and destination have {lat,lng} coords
  const validRides = rides.filter(
    r => typeof r.pickup?.coordinates?.lat === 'number' &&
         typeof r.destination?.coordinates?.lat === 'number'
  );
  const validDrivers = drivers.filter(d => d.location?.coordinates?.length === 2);

  const statusDot: Record<ConnStatus, string> = {
    connecting: '#9ca3af', live: '#22c55e', polling: '#f59e0b', error: '#ef4444',
  };
  const statusLabel: Record<ConnStatus, string> = {
    connecting: 'Connecting…', live: 'Live', polling: 'Polling 5s', error: 'Error',
  };

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 20 }}>
          <span style={{ fontSize: 14 }}>
            <strong style={{ color: '#22c55e' }}>{validDrivers.length}</strong> Online Drivers
          </span>
          <span style={{ fontSize: 14 }}>
            <strong style={{ color: '#f97316' }}>{validRides.length}</strong> Active Rides
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusDot[connStatus], display: 'inline-block' }} />
            {statusLabel[connStatus]}
          </span>
          <span style={{ fontSize: 12, color: error ? '#ef4444' : '#9ca3af' }}>
            {error || (lastUpdated && `Updated ${lastUpdated}`)}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#9ca3af', flexWrap: 'wrap', alignItems: 'center' }}>
        {([['#22c55e', 'Available'], ['#f97316', 'On a ride']] as [string, string][]).map(([c, l]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />
            {l}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', outline: '2px solid #22c55e', outlineOffset: 2, display: 'inline-block' }} />
          Recently moved
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, background: '#3b82f6', borderRadius: 2, display: 'inline-block' }} />
          Pickup
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, background: '#ef4444', borderRadius: 2, display: 'inline-block' }} />
          Dropoff
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 22, height: 3, background: '#3b82f6', borderRadius: 2, display: 'inline-block' }} />
          En-route to pickup
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 22, height: 3, background: '#22c55e', borderRadius: 2, display: 'inline-block' }} />
          Trip in progress
        </span>
      </div>

      {/* Map */}
      <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden' }}>
        {!isLoaded ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2937', color: '#9ca3af' }}>
            Loading map…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={center}
            zoom={12}
            onLoad={map => {
              mapRef.current = map;
              if (!hasFitRef.current) fitAllBounds(driversRef.current);
            }}
            options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false }}
          >
            {/* Admin's own location */}
            {adminLocation && (
              <Marker
                position={adminLocation}
                title="Your location"
                zIndex={999}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                      <circle cx="14" cy="14" r="13" fill="#4285F4" opacity="0.15"/>
                      <circle cx="14" cy="14" r="8" fill="#4285F4" stroke="white" stroke-width="2.5"/>
                      <circle cx="14" cy="14" r="3.5" fill="white"/>
                    </svg>`
                  )}`,
                  scaledSize: new window.google.maps.Size(28, 28),
                  anchor: new window.google.maps.Point(14, 14),
                }}
              />
            )}

            {/* Driver dots */}
            {validDrivers.map(d => {
              const pos = toLatLng(d.location.coordinates);
              if (!pos) return null;
              return (
                <Marker
                  key={d._id}
                  position={pos}
                  zIndex={10}
                  icon={makeDriverIcon(d.isAvailable, recentlyMoved.has(d._id))}
                  onClick={() => { setSelectedDriver(d); setSelectedRide(null); }}
                />
              );
            })}

            {/* Active ride routes + pins */}
            {validRides.map(r => {
              const pickup = rideCoord(r.pickup.coordinates);
              const dropoff = rideCoord(r.destination?.coordinates);
              if (!pickup || !dropoff) return null;
              const isEnRoute = EN_ROUTE_STATUSES.includes(r.status);
              const routePath = routePaths.get(r._id);
              const lineColor = isEnRoute ? '#3b82f6' : '#22c55e';

              // For en-route fallback line: driver position → pickup
              const driverPos = isEnRoute
                ? toLatLng(validDrivers.find(d => d._id === r.driver?._id)?.location.coordinates)
                : null;
              const fallbackStart = isEnRoute ? (driverPos ?? pickup) : pickup;
              const fallbackEnd = isEnRoute ? pickup : dropoff;

              return (
                <React.Fragment key={r._id}>
                  <Polyline
                    path={routePath ?? [fallbackStart, fallbackEnd]}
                    options={{
                      strokeColor: lineColor,
                      strokeOpacity: 0.9,
                      strokeWeight: 6,
                      geodesic: true,
                      zIndex: 1,
                    }}
                  />
                  <Marker
                    position={pickup}
                    zIndex={5}
                    title={`Pickup${r.pickup.address ? ': ' + r.pickup.address : ''}`}
                    onClick={() => { setSelectedRide(r); setSelectedDriver(null); }}
                    icon={pinIcon('#3b82f6')}
                  />
                  <Marker
                    position={dropoff}
                    zIndex={5}
                    title={`Dropoff${r.destination.address ? ': ' + r.destination.address : ''}`}
                    onClick={() => { setSelectedRide(r); setSelectedDriver(null); }}
                    icon={pinIcon('#ef4444')}
                  />
                </React.Fragment>
              );
            })}

            {/* Driver info window */}
            {selectedDriver && toLatLng(selectedDriver.location.coordinates) && (
              <InfoWindow
                position={toLatLng(selectedDriver.location.coordinates)!}
                onCloseClick={() => setSelectedDriver(null)}
              >
                <div style={{ fontSize: 13, lineHeight: '1.6' }}>
                  <strong>{selectedDriver.name}</strong><br />
                  {selectedDriver.phone}<br />
                  <span style={{ color: selectedDriver.isAvailable ? '#16a34a' : '#ea580c' }}>
                    {selectedDriver.isAvailable ? 'Available' : 'On a ride'}
                  </span>
                  {recentlyMoved.has(selectedDriver._id) && (
                    <><br /><span style={{ fontSize: 11, color: '#6366f1' }}>Just moved</span></>
                  )}
                </div>
              </InfoWindow>
            )}

            {/* Ride info window */}
            {selectedRide && rideCoord(selectedRide.pickup.coordinates) && (
              <InfoWindow
                position={rideCoord(selectedRide.pickup.coordinates)!}
                onCloseClick={() => setSelectedRide(null)}
              >
                <div style={{ fontSize: 13, lineHeight: '1.6' }}>
                  <strong>Ride — {selectedRide.status.replace('_', ' ')}</strong><br />
                  {selectedRide.rider && <>{selectedRide.rider.name}<br /></>}
                  {selectedRide.driver && <>{selectedRide.driver.name}<br /></>}
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    {selectedRide.pickup.address || 'Pickup'} → {selectedRide.destination?.address || 'Dropoff'}
                  </span>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>
    </div>
  );
};

export default LiveMap;
